import { Button, Input, Modal, Select, cn, useToast } from '@nexo/ui';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { useAuth, useMe } from '../auth/AuthProvider';
import { EmojiPicker } from '../chat/EmojiPicker';
import { errorMessage } from '../../lib/forms';

const presets = [
  { emoji: '📅', text: 'En una reunión', expires: '1h' },
  { emoji: '🚌', text: 'De camino', expires: '30m' },
  { emoji: '🤒', text: 'De baja', expires: 'today' },
  { emoji: '🏖️', text: 'De vacaciones', expires: 'never' },
  { emoji: '🏠', text: 'Teletrabajando', expires: 'today' },
  { emoji: '🎯', text: 'Concentrado', expires: '2h' },
] as const;

type Expiry = '30m' | '1h' | '2h' | 'today' | 'week' | 'never';

const expiryLabels: Record<Expiry, string> = {
  '30m': '30 minutos',
  '1h': '1 hora',
  '2h': '2 horas',
  today: 'Hoy',
  week: 'Esta semana',
  never: 'No borrar',
};

function expiryDate(expiry: Expiry, now = new Date()): string | null {
  const date = new Date(now);
  switch (expiry) {
    case '30m':
      return new Date(now.getTime() + 30 * 60_000).toISOString();
    case '1h':
      return new Date(now.getTime() + 60 * 60_000).toISOString();
    case '2h':
      return new Date(now.getTime() + 120 * 60_000).toISOString();
    case 'today':
      date.setHours(23, 59, 59, 0);
      return date.toISOString();
    case 'week':
      date.setDate(date.getDate() + ((7 - date.getDay()) % 7));
      date.setHours(23, 59, 59, 0);
      return date.toISOString();
    case 'never':
      return null;
  }
}

/** Set or clear the custom status. */
export function StatusDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const me = useMe();
  const { setUser } = useAuth();
  const toast = useToast();
  const [emoji, setEmoji] = useState(me.status?.emoji ?? '💬');
  const [text, setText] = useState(me.status?.text ?? '');
  const [expiry, setExpiry] = useState<Expiry>('today');
  const { t } = useTranslation();

  const save = useMutation({
    mutationFn: () => api.users.setStatus({ emoji, text, expiresAt: expiryDate(expiry) }),
    onSuccess: (updated) => {
      setUser(updated);
      onClose();
    },
    onError: (err) =>
      toast({ title: t('No se pudo guardar'), description: errorMessage(err), tone: 'error' }),
  });
  const clear = useMutation({
    mutationFn: api.users.clearStatus,
    onSuccess: (updated) => {
      setUser(updated);
      setText('');
      onClose();
    },
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('Tu estado')}
      size="sm"
      footer={
        <>
          {me.status && (
            <Button variant="ghost" onClick={() => clear.mutate()} loading={clear.isPending}>
              {t('Borrar estado')}
            </Button>
          )}
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!emoji}>
            {t('Guardar')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-2">
          <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-surface-2 text-lg">
            {emoji}
          </span>
          <EmojiPicker placement="bottom" label={t('Elegir emoji')} onPick={setEmoji} />
          <Input
            aria-label={t('Texto del estado')}
            placeholder={t('¿Qué estás haciendo?')}
            maxLength={100}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </div>
        <div className="grid grid-cols-2 gap-1.5">
          {presets.map((preset) => (
            <button
              key={preset.text}
              type="button"
              onClick={() => {
                setEmoji(preset.emoji);
                setText(t(preset.text));
                setExpiry(preset.expires);
              }}
              className={cn(
                'flex items-center gap-2 rounded-lg border px-2.5 py-2 text-left text-sm hover:bg-surface-2',
                text === t(preset.text) ? 'border-accent bg-accent-soft' : 'border-border',
              )}
            >
              <span>{preset.emoji}</span>
              {t(preset.text)}
            </button>
          ))}
        </div>
        <label className="flex items-center justify-between gap-3 text-sm">
          {t('Borrar después de')}
          <Select
            aria-label={t('Borrar el estado')}
            value={expiry}
            onChange={setExpiry}
            className="w-40!"
            options={(Object.keys(expiryLabels) as Expiry[]).map((key) => ({
              value: key,
              label: t(expiryLabels[key]),
            }))}
          />
        </label>
      </div>
    </Modal>
  );
}
